
import React, { useRef, useEffect, useMemo } from "react";
import { ChatMessage as Message, ProductInfo } from "./types";
import { ChatMessage } from "./ChatMessage";
import { useAuth } from "@/context/AuthContext";

interface ChatMessageListProps {
  messages: Message[];
  loading: boolean;
  currentUserId: string;
  onEditStart: (messageId: string, text: string) => void;
  onDelete: (messageId: string) => void;
  products?: any[];
}

export const ChatMessageList: React.FC<ChatMessageListProps> = ({
  messages,
  loading,
  currentUserId,
  onEditStart,
  onDelete,
  products
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Build a SKU → product lookup map
  const productMap = useMemo(() => {
    const map = new Map<string, ProductInfo>();
    if (products) {
      for (const p of products) {
        if (p.product_sku) {
          map.set(p.product_sku, p);
        }
      }
    }
    return map;
  }, [products]);

  // Try to match a message to a product by checking if it starts with a known SKU
  const getProductForMessage = (msg: Message): ProductInfo | null => {
    if (!msg.message || productMap.size === 0) return null;
    for (const [sku, product] of productMap) {
      if (msg.message.startsWith(sku + ":") || msg.message.startsWith(sku + " ")) {
        return product;
      }
    }
    return null;
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('nl-NL', { 
      hour: '2-digit', 
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getMessageClasses = (message: Message) => {
    let baseClasses = "p-3 rounded-md max-w-[80%]";
    
    if (message.is_deleted) {
      return `${baseClasses} bg-muted italic text-muted-foreground`;
    }
    
    return `${baseClasses} ${message.user_id === currentUserId 
      ? "bg-green-100 dark:bg-green-900/30" 
      : "bg-blue-100 dark:bg-blue-900/30"}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex justify-center items-center h-full text-muted-foreground">
        Nog geen berichten. Start een gesprek!
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 max-h-[280px] sm:max-h-[400px] overflow-y-auto">
      {messages.map((message) => (
        <ChatMessage
          key={message.id}
          message={message}
          formatTimestamp={formatTimestamp}
          getInitials={getInitials}
          getMessageClasses={getMessageClasses}
          onEdit={() => {}}
          onDelete={onDelete}
          isCurrentUser={currentUserId === message.user_id}
          editingMessage={null}
          editText=""
          setEditText={() => {}}
          setEditingMessage={(messageId) => {
            if (messageId) {
              onEditStart(messageId, message.message);
            }
          }}
          productInfo={getProductForMessage(message)}
        />
      ))}
      <div ref={messagesEndRef} />
    </div>
  );
};
